import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { sfx } from "../../lib/sfx";
import { usePrefersReducedMotion } from "../../hooks/useBreakpoint";

const CELL = 64;
const MIN_LOOPS = 4;
const MAX_POOL = 24;

function buildReelPool(ticketNos: number[], winnerTicketNo: number): number[] {
  const unique = [...new Set(ticketNos.filter((n) => Number.isFinite(n) && n > 0))];
  if (!unique.length) return [winnerTicketNo];

  if (unique.length <= MAX_POOL) {
    return unique.includes(winnerTicketNo) ? unique : [...unique, winnerTicketNo];
  }

  const sample: number[] = [];
  const step = Math.max(1, Math.floor(unique.length / (MAX_POOL - 1)));
  for (let i = 0; i < unique.length && sample.length < MAX_POOL - 1; i += step) {
    sample.push(unique[i]!);
  }
  if (!sample.includes(winnerTicketNo)) sample.push(winnerTicketNo);
  return sample;
}

/**
 * Slot-machine reel of ticket numbers. Spins fast, decelerates, and lands on
 * the winning ticket. Cells are real ticket numbers from the pool.
 */
export function TicketReel({
  ticketNos,
  winnerTicketNo,
  durationMs = 3400,
  onLanded,
}: {
  ticketNos: number[];
  winnerTicketNo: number;
  durationMs?: number;
  onLanded?: () => void;
}) {
  const reduced = usePrefersReducedMotion();
  const [spinning, setSpinning] = useState(!reduced);
  const timersRef = useRef<number[]>([]);
  const onLandedRef = useRef(onLanded);
  onLandedRef.current = onLanded;
  const spinKey = `${ticketNos.join(",")}:${winnerTicketNo}`;

  const pool = useMemo(() => buildReelPool(ticketNos, winnerTicketNo), [ticketNos, winnerTicketNo]);

  const winnerIdx = Math.max(0, pool.indexOf(winnerTicketNo));
  const strip = useMemo(() => {
    const loops = MIN_LOOPS;
    const cells: number[] = [];
    for (let l = 0; l < loops; l += 1) cells.push(...pool);
    cells.push(...pool.slice(0, winnerIdx + 1));
    return cells;
  }, [pool, winnerIdx]);

  const targetIndex = strip.length - 1;
  const travel = targetIndex * CELL;

  useEffect(() => {
    setSpinning(!reduced);
    if (reduced) {
      onLandedRef.current?.();
      return;
    }
    const steps = Math.min(targetIndex, 44);
    for (let i = 1; i <= steps; i += 1) {
      const p = i / steps;
      const t = 1 - Math.pow(1 - p, 1 / 3);
      timersRef.current.push(
        window.setTimeout(() => sfx.tick(1 + p * 0.6), t * durationMs),
      );
    }
    timersRef.current.push(
      window.setTimeout(() => {
        setSpinning(false);
        onLandedRef.current?.();
      }, durationMs),
    );
    const timers = timersRef.current;
    return () => {
      timers.forEach(window.clearTimeout);
      timersRef.current = [];
    };
  }, [spinKey, reduced, durationMs, targetIndex]);

  return (
    <div className="relative w-full max-w-xs">
      <div className="pointer-events-none absolute inset-x-0 top-1/2 z-20 h-16 -translate-y-1/2 rounded-xl border-2 border-brand-pink/70 glow-pink" />
      <div className="pointer-events-none absolute inset-x-0 top-1/2 z-20 -translate-y-1/2">
        <span className="absolute -left-3 top-1/2 -translate-y-1/2 border-y-8 border-l-8 border-y-transparent border-l-brand-pink" />
        <span className="absolute -right-3 top-1/2 -translate-y-1/2 border-y-8 border-r-8 border-y-transparent border-r-brand-pink" />
      </div>

      <div
        className="relative h-48 overflow-hidden rounded-2xl border border-white/10 bg-black/50"
        style={{
          maskImage: "linear-gradient(180deg, transparent, #000 22%, #000 78%, transparent)",
        }}
      >
        <motion.div
          key={spinKey}
          className={spinning ? "reel-blur" : ""}
          style={{ paddingTop: 96 - CELL / 2 }}
          initial={{ y: 0 }}
          animate={{ y: -travel }}
          transition={reduced ? { duration: 0 } : { duration: durationMs / 1000, ease: [0.08, 0.82, 0.16, 1] }}
        >
          {strip.map((no, i) => (
            <div
              key={i}
              className="flex items-center justify-center font-display text-2xl font-extrabold tabular-nums"
              style={{ height: CELL }}
            >
              <span className={i === targetIndex && !spinning ? "text-gradient" : "text-white/70"}>#{no}</span>
            </div>
          ))}
        </motion.div>
      </div>

      <p className="mt-3 text-center text-[10px] uppercase tracking-[0.3em] text-white/35">
        {spinning ? "Rolling…" : "Winning ticket"}
      </p>
    </div>
  );
}
